/**
 * V91: 프로젝트 태그 마스터 관리 Hook
 *
 * 태그 종류:
 * - workType: 업무형태 (QA, 로컬, 개발 등)
 * - projectDomain: 프로젝트 분야 (게임, 블록체인, 웹서비스 등)
 *
 * V91 변경:
 * - 전사 공용 (companyCode 제거)
 * - reorderTags에 type 파라미터 추가
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { ProjectTagMaster, ProjectTagType, ProjectTagStatus } from '../types/project';

// ===== 타입 정의 =====

/**
 * V91: 전사 공용 (companyCode 제거)
 */
export interface ListProjectTagsInput {
  type?: ProjectTagType;
  includeArchived?: boolean;
}

export interface ListProjectTagsOutput {
  success: boolean;
  tags: ProjectTagMaster[];
}

/**
 * V91: 전사 공용 (companyCode 제거)
 */
export interface UpsertProjectTagInput {
  id?: string; // 수정 시 필요
  type: ProjectTagType;
  label: string;
  status?: ProjectTagStatus;
  sortOrder?: number;
}

export interface UpsertProjectTagOutput {
  success: boolean;
  tagId: string;
}

export interface ArchiveProjectTagInput {
  id: string;
}

export interface ArchiveProjectTagOutput {
  success: boolean;
}

/**
 * V91: type 파라미터 추가
 */
export interface ReorderProjectTagsInput {
  type: ProjectTagType;
  tagIds: string[];
}

export interface ReorderProjectTagsOutput {
  success: boolean;
}

// ===== Hook =====

export function useProjectTags() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const functions = getFunctions();

  /**
   * 태그 목록 조회
   * V91: 전사 공용 (companyCode 불필요)
   */
  const listTags = useCallback(
    async (input?: ListProjectTagsInput): Promise<ProjectTagMaster[]> => {
      try {
        setIsLoading(true);
        setError(null);

        const listProjectTagsCF = httpsCallable<ListProjectTagsInput, ListProjectTagsOutput>(
          functions,
          'listProjectTags'
        );

        const result = await listProjectTagsCF(input || {});

        if (!result.data.success) {
          throw new Error('태그 목록 조회 실패');
        }

        return result.data.tags;
      } catch (err: any) {
        const errorMessage = err.message || '태그 목록 조회 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  /**
   * 태그 생성/수정
   * V91: 전사 공용 (companyCode 불필요)
   */
  const upsertTag = useCallback(
    async (input: UpsertProjectTagInput): Promise<string> => {
      try {
        setIsLoading(true);
        setError(null);

        const upsertProjectTagCF = httpsCallable<UpsertProjectTagInput, UpsertProjectTagOutput>(
          functions,
          'upsertProjectTag'
        );

        const result = await upsertProjectTagCF(input);

        if (!result.data.success) {
          throw new Error('태그 저장 실패');
        }

        return result.data.tagId;
      } catch (err: any) {
        const errorMessage = err.message || '태그 저장 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  /**
   * 태그 비활성화 (archived)
   */
  const archiveTag = useCallback(
    async (id: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const archiveProjectTagCF = httpsCallable<ArchiveProjectTagInput, ArchiveProjectTagOutput>(
          functions,
          'archiveProjectTag'
        );

        const result = await archiveProjectTagCF({ id });

        if (!result.data.success) {
          throw new Error('태그 비활성화 실패');
        }
      } catch (err: any) {
        const errorMessage = err.message || '태그 비활성화 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  /**
   * 태그 정렬 순서 변경
   * V91: type 파라미터 추가
   */
  const reorderTags = useCallback(
    async (type: ProjectTagType, tagIds: string[]): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const reorderProjectTagsCF = httpsCallable<ReorderProjectTagsInput, ReorderProjectTagsOutput>(
          functions,
          'reorderProjectTags'
        );

        const result = await reorderProjectTagsCF({ type, tagIds });

        if (!result.data.success) {
          throw new Error('태그 순서 변경 실패');
        }
      } catch (err: any) {
        const errorMessage = err.message || '태그 순서 변경 중 오류가 발생했습니다.';
        setError(new Error(errorMessage));
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [functions]
  );

  return {
    listTags,
    upsertTag,
    archiveTag,
    reorderTags,
    isLoading,
    error,
  };
}
